import { useMemo } from 'react'
import { AdminAPI, getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import { dateTime } from '@guestgoodie/helpers'
import { skipToken, useSuspenseQuery } from '@apollo/client'
import { Trans, useLingui } from '@lingui/react/macro'

import * as Form from '@/features/form'
import Button from '@/components/ui/Button'
import Dropdown from '@/components/ui/Dropdown'
import DateTimeInput from '@/components/ui/DateTimeInput'
import GuestsField from './GuestsField'
import OrdersField from './OrdersField'
import { useStateOptions } from '../utils/helper'
import useStayForm, { FormData } from '../hooks/useStayForm'
import { Permission, useCheckPermission } from '@/features/auth'
import { useIsConnected } from '@/features/pms'
import { SelectSpace } from '@/features/space'

type CreateProps = {
  onDone: () => void
  onCancel: () => void
}

type EditProps = {
  id: number
  isCheckIn?: boolean
  isCheckOut?: boolean
  onDone: () => void
  onCancel: () => void
}

type Props = CreateProps | EditProps

export default function StayForm(props: Props) {
  const { onDone, onCancel } = props
  const id = (props as EditProps).id ?? null

  const hasUpdatePermission = useCheckPermission([Permission.STAY_UPDATE])
  const isConnectedToPms = useIsConnected()
  const canUpdate = hasUpdatePermission && !isConnectedToPms

  const { t } = useLingui()
  const stateOptions = useStateOptions()

  const { data: stayData } = useSuspenseQuery(
    AdminAPI.stay.GET,
    id
      ? {
          variables: {
            id,
            withGuests: true,
            withSpace: true
          }
        }
      : skipToken
  )

  const stay = useMemo(
    () => getFragment(SharedAPI.stay.STAY_FRAGMENT, stayData?.getStay),
    [stayData]
  )

  const { errors, create, update } = useStayForm({
    onDone,
    id
  })

  const defaultValues = useMemo(() => {
    const now = dateTime.nowInUTC()
    const tomorrow = new Date(now.getTime() + 24 * 60 * 60_000)

    const state = () => {
      if ((props as EditProps).isCheckIn) {
        return GraphQL.StayStates.Staying
      }

      if ((props as EditProps).isCheckOut) {
        return GraphQL.StayStates.CheckedOut
      }

      return stay?.state ?? GraphQL.StayStates.Scheduled
    }

    return {
      spaceId: stay?.spaceId ?? null,
      state: state(),
      scheduledStart: stay?.scheduledStart
        ? new Date(stay.scheduledStart)
        : dateTime.nowInUTC(),
      scheduledStop: stay?.scheduledStop
        ? new Date(stay.scheduledStop)
        : tomorrow,
      guestIds:
        stay?.guests?.map(
          guest => getFragment(SharedAPI.guest.GUEST_FRAGMENT, guest).id
        ) ?? []
    }
  }, [stay, props])

  return (
    <Form.Root<FormData>
      onSubmit={stay ? update : create}
      serverErrors={errors}
      defaultValues={defaultValues}
      readOnly={!canUpdate}
    >
      <div className="flex flex-col gap-7 p-2.5">
        <Form.Field
          name="state"
          label={`${t`Status`}*`}
          rules={{
            required: t`This field is required`
          }}
        >
          {({ renderProps: props }) => (
            <Dropdown options={stateOptions} {...props} />
          )}
        </Form.Field>
        <Form.Field
          name="scheduledStart"
          label={`${t`Check-In`}*`}
          rules={{
            required: t`This field is required`
          }}
        >
          {({ renderProps: props }) => <DateTimeInput {...props} />}
        </Form.Field>
        <Form.Field
          name="scheduledStop"
          label={`${t`Check-Out`}*`}
          rules={{
            required: t`This field is required`
          }}
        >
          {({ renderProps: props }) => <DateTimeInput {...props} />}
        </Form.Field>
        <Form.Field name="spaceId" label={`${t`Room`}*`}>
          {({ renderProps: { value, ...props } }) => (
            <SelectSpace id={value} placeholder={t`No room`} {...props} />
          )}
        </Form.Field>
        <Form.Field name="guestIds">
          {({ renderProps: props }) => <GuestsField {...props} />}
        </Form.Field>
        <Form.Data<FormData>>
          {({ state }) => {
            if (!stay) return null
            if (stay.state === state) return null
            if (state !== GraphQL.StayStates.CheckedOut) return null

            return <OrdersField stayId={stay.id} />
          }}
        </Form.Data>
        <footer className="flex flex-row items-center justify-between gap-4">
          <Button variant="outline" onClick={onCancel} type="button">
            <Trans>Cancel</Trans>
          </Button>
          <Form.Submit>
            <Trans>Save</Trans>
          </Form.Submit>
        </footer>
      </div>
    </Form.Root>
  )
}
